/* eslint-disable react/no-multi-comp */

import React, {
    useState,
    useCallback,
    forwardRef,
    useRef,
    useMemo,
} from 'react';
import { Table, type TableProps, type ColumnType } from '@app/ui';

import ResizableTitle from './ResizableTitle';
import { TableFilters } from './TableFilters/TableFilters';
import { filterByFilterMethod } from './TableFilters/TableFilterMethods';

import 'react-resizable/css/styles.css';
import './ResizableTable.scss';

// Generic, typed, resizable table with forwardRef
function ResizableTableInner<RecordType extends object = any>(
    props: TableProps<RecordType>,
    ref: React.Ref<any>,
) {
    const { columns = [], dataSource = [], ...rest } = props;
    const [filters, setFilters] = useState<
        Record<
            string,
            { type: 'text' | 'select'; dataIndex: string; value: any }
        >
    >({});
    const filtersRef = useRef(filters);

    // Use a ref to persist column widths across re-renders
    const persistedWidths = useRef<Record<string, number>>({});

    // Initialize columns with width, preserving any previously set widths
    const [columnsWithWidth, setColumnsWithWidth] = useState(() =>
        (columns as ColumnType<RecordType>[]).map(col => {
            const colKey = String(col.key || col.dataIndex || '');
            const persistedWidth = persistedWidths.current[colKey];
            return {
                ...col,
                width: persistedWidth || col.width || 150,
            };
        }),
    );

    // Update columns if prop changes, preserving persisted widths
    React.useEffect(() => {
        setColumnsWithWidth(
            (columns as ColumnType<RecordType>[]).map(col => {
                const colKey = String(col.key || col.dataIndex || '');
                const persistedWidth = persistedWidths.current[colKey];
                return {
                    ...col,
                    width: persistedWidth || col.width || 150,
                };
            }),
        );
    }, [columns]);

    // Handle column resize
    const handleResize = useCallback(
        (index: number) =>
            (
                _e: React.SyntheticEvent<Element>,
                { size }: { size: { width: number } },
            ) => {
                setColumnsWithWidth(prev => {
                    const next = [...prev];
                    const col = next[index];
                    const colKey = String(col.key || col.dataIndex || '');

                    // Persist the width
                    persistedWidths.current[colKey] = size.width;

                    next[index] = {
                        ...next[index],
                        width: size.width,
                    };
                    return next;
                });
            },
        [],
    );

    const handleFilterChange = useCallback(
        (
            id: string,
            type: 'text' | 'select',
            dataIndex: string,
            value: any,
        ) => {
            setFilters(prevFilters => {
                const nextFilters = {
                    ...prevFilters,
                    [id]: {
                        type,
                        value,
                        dataIndex,
                    },
                };

                // Keep ref synchronized in the same tick so header filter
                // inputs render the latest typed value without lag.
                filtersRef.current = nextFilters;

                return nextFilters;
            });
        },
        [],
    );

    const filteredDataSource = useMemo(() => {
        if (!dataSource.length) return [];

        return dataSource.filter(record => {
            if (!Object.keys(filters)?.length) return true;

            const filterPasses = [];

            for (const filter in filters) {
                const { type, dataIndex, value } = filters[filter];

                const filterMethod = filterByFilterMethod(type);

                filterPasses.push(filterMethod(record, dataIndex, value));
            }

            return filterPasses.every(
                singleFilterPass => singleFilterPass === true,
            );
        });
    }, [dataSource, filters]);

    // Enhance columns with onHeaderCell
    const resizableColumns = columnsWithWidth.map((col, index) => ({
        ...col,
        onHeaderCell: (column: any) => ({
            width: column.width,
            minWidth: column.minWidth,
            onResize: handleResize(index),
        }),
    })) as any; // Use any to bypass strict typing for the columns array

    const headerWrapper = useCallback(
        props => (
            <TableFilters
                {...props}
                columns={columns}
                onFilter={handleFilterChange}
                filters={filtersRef.current}
            />
        ),
        [columns, handleFilterChange],
    );

    const components = useMemo(
        () => ({
            header: {
                cell: ResizableTitle,
                wrapper: headerWrapper,
            },
        }),
        [headerWrapper],
    );

    return (
        <Table
            ref={ref}
            {...rest}
            dataSource={filteredDataSource}
            columns={resizableColumns}
            components={components}
        />
    );
}

const ResizableTable = forwardRef(ResizableTableInner) as <
    RecordType extends object = any,
>(
    props: TableProps<RecordType> & { ref?: React.Ref<any> },
) => React.ReactElement;

export default ResizableTable;
